import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AxiosPostMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { Link, useNavigate } from "react-router-dom";
import convertoformdata from "../helpers/convertToFormdata";
import { setShowPreloader } from "../store/activePage";
import { useDispatch, useSelector } from "react-redux";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarCheck,
  FaEye,
  FaEyeSlash,
  FaMapMarkerAlt,
  FaMobileAlt,
  FaUserCircle,
} from "react-icons/fa";
import CitySearchSelect from "../components/city-search-select";
import { setTypingMode } from "../store/activePage";

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-100";

const errorClass = "mt-1.5 text-xs font-semibold text-red-600";

const FormField = ({ label, name, type = "text", forceTamil = false, noTranslate = false, children }) => (
  <div>
    <label htmlFor={name} className="mb-1.5 block text-sm font-bold text-slate-700">
      {label}
    </label>
    {children || (
      <Field
        id={name}
        name={name}
        type={type}
        className={`${inputClass} ${forceTamil ? "force-tamil" : ""} ${noTranslate ? "no-translate" : ""}`}
      />
    )}
    <ErrorMessage name={name} component="div" className={errorClass} />
  </div>
);

const CustomerRegister = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const typingMode = useSelector((state) => state.activePage.typingMode || "tanglish");

  const initialValues = {
    first_name: "",
    last_name: "",
    occupation: "",
    mobile: "",
    email: "",
    password: "",
    plot_no: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  };

  const validationSchema = Yup.object({
    first_name: Yup.string()
      .required("First Name is required"),
    last_name: Yup.string()
      .required("Last Name is required"),
    occupation: Yup.string()
      .required("Occupation is required"),
    mobile: Yup.string()
      .matches(/^\d{10}$/, "Mobile number must be 10 digits")
      .required("Mobile number is required"),
    email: Yup.string().email("Invalid email address"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    plot_no: Yup.string().required("Plot No. is required"),
    address: Yup.string()
      .required("Address is required"),
    city: Yup.string()
      .required("City is required"),
    state: Yup.string()
      .required("State is required"),
    pincode: Yup.string()
      .matches(/^\d{6}$/, "Pincode must be 6 digits")
      .required("Pincode is required"),
  });

  const toggleTamilTyping = () => {
    const newMode = typingMode === "tanglish" ? "english" : "tanglish";
    dispatch(setTypingMode(newMode));
  };

  const onSubmit = async (values, { setErrors }) => {
    const bodyObj = {
      first_name: values.first_name,
      last_name: values.last_name,
      occupation: values.occupation,
      mobile: values.mobile,
      email: values.email,
      password: values.password,
      plot_no: values.plot_no,
      address: values.address,
      city: values.city,
      state: values.state,
      pincode: values.pincode,
    };
    const body = convertoformdata(bodyObj);

    try {
      dispatch(setShowPreloader(true));
      const cusRes = await AxiosPostMethod("customer/registration", body, true);
      dispatch(setShowPreloader(false));

      if (cusRes?.status === 200) {
        localStorage.setItem("otp-verification-mobile", values.mobile);
        SuccessToast("Account created successfully, Please login to dashboard");
        navigate("/login");
        return;
      }

      // Handle registration conflict (409) specifically if returned as a successful response
      if (cusRes?.status === 409) {
        const availability = cusRes?.data?.data;
        const errors = {};

        if (availability?.mobile_exists) {
          errors.mobile = "This mobile number is already registered.";
        }
        if (availability?.email_exists) {
          errors.email = "This email is already registered.";
        }

        if (Object.keys(errors).length > 0) {
          setErrors(errors);
        }

        ErrorToast(
          cusRes?.data?.message ||
            "Registration failed: Mobile number or email already in use."
        );
        return;
      }

      const errorMessage =
        cusRes?.data?.data?.error ||
        cusRes?.data?.message ||
        cusRes?.data?.error ||
        "Unable to complete registration. Please try again.";

      ErrorToast(errorMessage);
    } catch (error) {
      dispatch(setShowPreloader(false));

      // Handle thrown 409 Conflict error from Axios
      if (error.response?.status === 409) {
        const availability = error.response?.data?.data;
        const errors = {};

        if (availability?.mobile_exists) {
          errors.mobile = "This mobile number is already registered.";
        }
        if (availability?.email_exists) {
          errors.email = "This email is already registered.";
        }

        if (Object.keys(errors).length > 0) {
          setErrors(errors);
        }
      }

      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.data?.error;

      ErrorToast(
        serverMessage || "Something went wrong while registering. Please try again."
      );
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Tamil Typing
            </span>
            <button
              type="button"
              onClick={toggleTamilTyping}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                typingMode === "tanglish" ? "bg-sky-600" : "bg-slate-300"
              }`}
              aria-label="Toggle Tamil typing"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                  typingMode === "tanglish" ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
          <Link
            to="/login"
            className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 no-underline shadow-sm transition hover:border-slate-300 hover:text-slate-950"
          >
            <FaArrowLeft className="text-xs" />
            Back to login
          </Link>
          <p className="text-sm font-semibold text-slate-500">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-bold text-sky-700 no-underline hover:text-sky-900"
            >
              Sign in
            </Link>
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/70">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr]">
            <aside className="bg-slate-950 p-8 text-white lg:p-10">
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-300">
                Customer registration
              </p>
              <h1 className="mt-4 text-3xl font-black leading-tight tracking-tight">
                Create your moi customer account
              </h1>
              <p className="mt-4 text-sm leading-7 text-slate-300">
                Register once to create tokens, view upcoming events, and keep
                your moi records organised.
              </p>

              <div className="mt-8 grid gap-3">
                {[
                  { icon: FaMobileAlt, text: "Mobile based account access" },
                  { icon: FaCalendarCheck, text: "Event and token management" },
                  { icon: FaMapMarkerAlt, text: "Address details for records" },
                ].map(({ icon: Icon, text }) => (
                  <div
                    key={text}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3"
                  >
                    <Icon className="text-sky-300" />
                    <span className="text-sm font-semibold text-slate-200">
                      {text}
                    </span>
                  </div>
                ))}
              </div>
            </aside>

            <div className="p-5 sm:p-8 lg:p-10">
              <div className="mb-7 flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                  <FaUserCircle className="text-xl" />
                </span>
                <div>
                  <h2 className="text-2xl font-black tracking-tight text-slate-950">
                    Personal Details
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Enter your contact and address details to set up the
                    account.
                  </p>
                </div>
              </div>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ isSubmitting, values, setFieldValue, setFieldTouched }) => (
                  <Form className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField label="First Name" name="first_name" forceTamil />
                      <FormField label="Last Name" name="last_name" forceTamil />
                      <FormField label="Occupation" name="occupation" forceTamil />
                      <FormField label="Mobile Number" name="mobile" noTranslate />
                      <FormField label="Email (Optional)" name="email" type="email" noTranslate />
                      <FormField label="Password" name="password">
                        <div className="relative">
                          <Field
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            className={`no-translate pr-11 ${inputClass}`}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-3 flex items-center text-slate-500 transition hover:text-slate-900"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showPassword ? (
                              <FaEyeSlash size={18} />
                            ) : (
                              <FaEye size={18} />
                            )}
                          </button>
                        </div>
                      </FormField>
                      <FormField label="Plot No." name="plot_no" forceTamil />
                    </div>

                    <FormField label="Address" name="address" forceTamil />

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <FormField label="City" name="city">
                        <CitySearchSelect
                          value={values.city}
                          onChange={(city) => setFieldValue("city", city)}
                          onBlur={() => setFieldTouched("city", true)}
                          className={`no-translate ${inputClass}`}
                          placeholder="Search city…"
                        />
                      </FormField>
                      <FormField label="State" name="state" forceTamil />
                      <FormField label="Pincode" name="pincode" noTranslate />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-300 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Register Account
                      <FaArrowRight className="text-xs" />
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegister;
